import React, { useState, useEffect } from 'react';
import {
    Palette as PaletteIcon,
    ColorLens as ColorLensIcon,
    Brush as BrushIcon,
    Save as SaveIcon,
    Refresh as RefreshIcon,
    Check as CheckIcon,
    Close as CloseIcon,
    ArrowBack as ArrowBackIcon,
    Warning as WarningIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../provider/UserProvider';
import axiosInstance from '../utlis/axiosInstance';
import styles from './ThemeManagement.module.css';

interface ColorScheme {
    id: string;
    name: string;
    primary: string;
    primaryLight: string;
    primaryDark: string;
    description: string;
}

const ThemeManagement: React.FC = () => {
    const navigate = useNavigate();
    const { updateTheme, resetUserTheme, theme, user } = useUser();
    const [selectedScheme, setSelectedScheme] = useState<string>('default');
    const [customPrimary, setCustomPrimary] = useState('#1D2B56');
    const [customPrimaryLight, setCustomPrimaryLight] = useState('#2a3a6a');
    const [customPrimaryDark, setCustomPrimaryDark] = useState('#162142');
    const [isCustomMode, setIsCustomMode] = useState(false);
    const [showPreview, setShowPreview] = useState(false);
    const [savedMessage, setSavedMessage] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    const predefinedSchemes: ColorScheme[] = [
        {
            id: 'default',
            name: 'Default Blue',
            primary: '#1D2B56',
            primaryLight: '#2a3a6a',
            primaryDark: '#162142',
            description: 'Professional navy blue theme'
        },
        {
            id: 'ocean',
            name: 'Ocean Blue',
            primary: '#0F4C75',
            primaryLight: '#3282B8',
            primaryDark: '#1B262C',
            description: 'Deep ocean blue tones'
        },
        {
            id: 'forest',
            name: 'Forest Green',
            primary: '#2D5A27',
            primaryLight: '#4A7C59',
            primaryDark: '#1B3D1F',
            description: 'Natural forest green theme'
        },
        {
            id: 'sunset',
            name: 'Sunset Orange',
            primary: '#D35400',
            primaryLight: '#E67E22',
            primaryDark: '#A04000',
            description: 'Warm sunset orange theme'
        },
        {
            id: 'royal',
            name: 'Royal Purple',
            primary: '#6A4C93',
            primaryLight: '#8B5CF6',
            primaryDark: '#4C1D95',
            description: 'Elegant royal purple theme'
        },
        {
            id: 'crimson',
            name: 'Crimson Red',
            primary: '#B91C1C',
            primaryLight: '#DC2626',
            primaryDark: '#7F1D1D',
            description: 'Bold crimson red theme'
        },
        {
            id: 'teal',
            name: 'Teal Blue',
            primary: '#0F766E',
            primaryLight: '#14B8A6',
            primaryDark: '#134E4A',
            description: 'Modern teal blue theme'
        },
        {
            id: 'amber',
            name: 'Amber Gold',
            primary: '#D97706',
            primaryLight: '#F59E0B',
            primaryDark: '#92400E',
            description: 'Rich amber gold theme'
        }
    ];

    useEffect(() => {
        // Load current theme from CSS variables
        const root = document.documentElement;
        const currentPrimary = getComputedStyle(root).getPropertyValue('--primary-color').trim();
        const currentPrimaryLight = getComputedStyle(root).getPropertyValue('--primary-light').trim();
        const currentPrimaryDark = getComputedStyle(root).getPropertyValue('--primary-dark').trim();

        // Check if current theme matches any predefined scheme
        const matchingScheme = predefinedSchemes.find(scheme =>
            scheme.primary.toLowerCase() === currentPrimary.toLowerCase() &&
            scheme.primaryLight.toLowerCase() === currentPrimaryLight.toLowerCase() &&
            scheme.primaryDark.toLowerCase() === currentPrimaryDark.toLowerCase()
        );

        if (matchingScheme) {
            setSelectedScheme(matchingScheme.id);
            setIsCustomMode(false);
        } else {
            setCustomPrimary(currentPrimary);
            setCustomPrimaryLight(currentPrimaryLight);
            setCustomPrimaryDark(currentPrimaryDark);
            setIsCustomMode(true);
        }
    }, []);

    const applyTheme = (primary: string, primaryLight: string, primaryDark: string) => {
        const root = document.documentElement;
        root.style.setProperty('--primary-color', primary);
        root.style.setProperty('--primary-light', primaryLight);
        root.style.setProperty('--primary-dark', primaryDark);

        // Force a re-render by dispatching a custom event
        window.dispatchEvent(new CustomEvent('themeChanged', {
            detail: { primary, primaryLight, primaryDark }
        }));
    };

    const handleSchemeSelect = async (scheme: ColorScheme) => {
        setSelectedScheme(scheme.id);
        setIsCustomMode(false);
        applyTheme(scheme.primary, scheme.primaryLight, scheme.primaryDark);
        setShowPreview(true);

        // Auto-save the selected theme
        const themeData = {
            primary: scheme.primary,
            primaryLight: scheme.primaryLight,
            primaryDark: scheme.primaryDark,
            isCustom: false,
            selectedScheme: scheme.id
        };
        try {
            await updateTheme(themeData);
        } catch (error) {
            console.error('Error saving theme:', error);
        }
    };

    const handleCustomColorChange = async (type: 'primary' | 'light' | 'dark', value: string) => {
        switch (type) {
            case 'primary':
                setCustomPrimary(value);
                break;
            case 'light':
                setCustomPrimaryLight(value);
                break;
            case 'dark':
                setCustomPrimaryDark(value);
                break;
        }

        if (isCustomMode) {
            const newPrimary = type === 'primary' ? value : customPrimary;
            const newPrimaryLight = type === 'light' ? value : customPrimaryLight;
            const newPrimaryDark = type === 'dark' ? value : customPrimaryDark;

            applyTheme(newPrimary, newPrimaryLight, newPrimaryDark);

            // Auto-save custom theme changes
            const themeData = {
                primary: newPrimary,
                primaryLight: newPrimaryLight,
                primaryDark: newPrimaryDark,
                isCustom: true,
                selectedScheme: 'custom'
            };
            try {
                await updateTheme(themeData);
            } catch (error) {
                console.error('Error saving theme:', error);
            }
        }
    };

    const handleCustomModeToggle = async () => {
        setIsCustomMode(!isCustomMode);
        if (!isCustomMode) {
            applyTheme(customPrimary, customPrimaryLight, customPrimaryDark);

            // Auto-save custom theme when enabling custom mode
            const themeData = {
                primary: customPrimary,
                primaryLight: customPrimaryLight,
                primaryDark: customPrimaryDark,
                isCustom: true,
                selectedScheme: 'custom'
            };
            try {
                await updateTheme(themeData);
            } catch (error) {
                console.error('Error saving theme:', error);
            }
        }
    };

    const handleSaveTheme = async () => {
        setError('');
        setSavedMessage('');
        setLoading(true);

        try {
            // Save to localStorage for persistence
            const selectedSchemeData = predefinedSchemes.find(s => s.id === selectedScheme);
            const primary = isCustomMode ? customPrimary : (selectedSchemeData?.primary || '#1D2B56');
            const primaryLight = isCustomMode ? customPrimaryLight : (selectedSchemeData?.primaryLight || '#2a3a6a');
            const primaryDark = isCustomMode ? customPrimaryDark : (selectedSchemeData?.primaryDark || '#162142');

            const themeData = {
                primary,
                primaryLight,
                primaryDark,
                isCustom: isCustomMode,
                selectedScheme: selectedScheme
            };

            // Apply the theme immediately
            applyTheme(primary, primaryLight, primaryDark);

            // Save to user profile via API
            if (user?.user?.id) {
                try {
                    console.log('Saving theme to user profile:', { userId: user.user?.id, themeData });
                    
                    // Fetch the latest user object
                    const userRes = await axiosInstance.get(`/gpstracker/userhandler/${user.user?.id}`);
                    let userObj = userRes.data?.responseData;
                    if (!userObj) {
                        throw new Error('Failed to fetch user details.');
                    }

                    // Update user attributes with theme data
                    const updatedUser = {
                        ...userObj,
                        attributes: {
                            ...userObj.attributes,
                            theme: themeData,
                        },
                    };

                    // Remove can_create_device field if it exists (similar to PointsTransfer.tsx)
                    delete updatedUser.can_create_device;

                    console.log('Updating user with theme data:', updatedUser);

                    // Send PUT request to update user
                    await axiosInstance.put(`/gpstracker/userhandler/${user.user?.id}`, updatedUser);
                    
                    console.log('Theme saved successfully to user profile');
                    setSavedMessage('Theme saved successfully to your profile!');
                } catch (err) {
                    console.error('Error saving theme to user profile:', err);
                    setError('Failed to save theme to profile. Theme saved locally only.');
                }
            } else {
                setSavedMessage('Theme saved locally (not logged in)');
            }

            // Also save to localStorage and user context
            await updateTheme(themeData);
            
        } catch (error) {
            console.error('Error saving theme:', error);
            setError('Error saving theme. Please try again.');
        } finally {
            setLoading(false);
            setTimeout(() => {
                setSavedMessage('');
                setError('');
            }, 3000);
        }
    };

    const handleResetTheme = async () => {
        setError('');
        setSavedMessage('');
        setLoading(true);

        try {
            const defaultScheme = predefinedSchemes.find(s => s.id === 'default')!;
            setSelectedScheme('default');
            setIsCustomMode(false);
            setCustomPrimary(defaultScheme.primary);
            setCustomPrimaryLight(defaultScheme.primaryLight);
            setCustomPrimaryDark(defaultScheme.primaryDark);
            applyTheme(defaultScheme.primary, defaultScheme.primaryLight, defaultScheme.primaryDark);

            // Save default theme to user profile
            if (user?.user?.id) {
                try {
                    const userRes = await axiosInstance.get(`/gpstracker/userhandler/${user.user?.id}`);
                    let userObj = userRes.data?.responseData;
                    if (!userObj) {
                        throw new Error('Failed to fetch user details.');
                    }

                    const defaultTheme = {
                        primary: defaultScheme.primary,
                        primaryLight: defaultScheme.primaryLight,
                        primaryDark: defaultScheme.primaryDark,
                        isCustom: false,
                        selectedScheme: 'default'
                    };

                    const updatedUser = {
                        ...userObj,
                        attributes: {
                            ...userObj.attributes,
                            theme: defaultTheme,
                        },
                    };

                    delete updatedUser.can_create_device;

                    await axiosInstance.put(`/gpstracker/userhandler/${user.user?.id}`, updatedUser);
                    setSavedMessage('Theme reset to default and saved to your profile!');
                } catch (err) {
                    console.error('Error saving default theme to user profile:', err);
                    setError('Failed to save default theme to profile. Theme reset locally only.');
                }
            } else {
                setSavedMessage('Theme reset to default locally (not logged in)');
            }

            await resetUserTheme();
        } catch (error) {
            console.error('Error resetting theme:', error);
            setError('Error resetting theme. Please try again.');
        } finally {
            setLoading(false);
            setTimeout(() => {
                setSavedMessage('');
                setError('');
            }, 3000);
        }
    };

    const generateLighterColor = (color: string, amount: number) => {
        const hex = color.replace('#', '');
        const r = Math.min(255, parseInt(hex.substr(0, 2), 16) + amount);
        const g = Math.min(255, parseInt(hex.substr(2, 2), 16) + amount);
        const b = Math.min(255, parseInt(hex.substr(4, 2), 16) + amount);
        return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
    };

    const generateDarkerColor = (color: string, amount: number) => {
        const hex = color.replace('#', '');
        const r = Math.max(0, parseInt(hex.substr(0, 2), 16) - amount);
        const g = Math.max(0, parseInt(hex.substr(2, 2), 16) - amount);
        const b = Math.max(0, parseInt(hex.substr(4, 2), 16) - amount);
        return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
    };

    const handleAutoGenerate = () => {
        const lighter = generateLighterColor(customPrimary, 30);
        const darker = generateDarkerColor(customPrimary, 30);
        setCustomPrimaryLight(lighter);
        setCustomPrimaryDark(darker);
        applyTheme(customPrimary, lighter, darker);
    };

    return (
        <div className={styles['theme-management-container']}>
            <div className={styles['theme-management-content']}>
                {/* Header */}
                <div className={styles['theme-header']}>
                    <div className={styles['header-banner']}>
                        <div className={styles['banner-content']}>
                            <div className={styles['banner-icon']}>
                                <PaletteIcon className={styles.icon} />
                            </div>
                            <div className={styles['banner-text']}>
                                <h1 className={styles['banner-title']}>Theme Management</h1>
                                <p className={styles['banner-subtitle']}>Customize your application's color scheme and visual appearance</p>
                            </div>
                            <button
                                onClick={() => navigate('/settings')}
                                className={styles['back-button']}
                            >
                                <ArrowBackIcon />
                                Back to Settings
                            </button>
                        </div>
                    </div>
                </div>

                {/* Success Message */}
                {savedMessage && (
                    <div className={styles['success-message']}>
                        <CheckIcon />
                        <span>{savedMessage}</span>
                        <button onClick={() => setSavedMessage('')}>
                            <CloseIcon />
                        </button>
                    </div>
                )}

                {/* Error Message */}
                {error && (
                    <div className={`${styles['alert']} ${styles['error']}`}>
                        <WarningIcon sx={{ fontSize: 18 }} />
                        {error}
                    </div>
                )}

                <div className={styles['theme-sections']}>
                    {/* Predefined Themes Section */}
                    <div className={styles['theme-section']}>
                        <div className={styles['section-header']}>
                            <h2 className={styles['section-title']}>
                                <ColorLensIcon className={styles['section-icon']} />
                                Predefined Themes
                            </h2>
                            <p className={styles['section-description']}>
                                Choose from our carefully crafted color schemes
                            </p>
                        </div>

                        <div className={styles['themes-grid']}>
                            {predefinedSchemes.map((scheme) => (
                                <div
                                    key={scheme.id}
                                    className={`${styles['theme-card']} ${selectedScheme === scheme.id && !isCustomMode ? styles.selected : ''}`}
                                    onClick={() => handleSchemeSelect(scheme)}
                                >
                                    <div className={styles['theme-preview']}>
                                        <div
                                            className={`${styles['color-preview']} ${styles.primary}`}
                                            style={{ backgroundColor: scheme.primary }}
                                        />
                                        <div
                                            className={`${styles['color-preview']} ${styles.light}`}
                                            style={{ backgroundColor: scheme.primaryLight }}
                                        />
                                        <div
                                            className={`${styles['color-preview']} ${styles.dark}`}
                                            style={{ backgroundColor: scheme.primaryDark }}
                                        />
                                    </div>
                                    <div className={styles['theme-info']}>
                                        <h3 className={styles['theme-name']}>{scheme.name}</h3>
                                        <p className={styles['theme-description']}>{scheme.description}</p>
                                    </div>
                                    {selectedScheme === scheme.id && !isCustomMode && (
                                        <div className={styles['selected-indicator']}>
                                            <CheckIcon />
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Custom Theme Section */}
                    <div className={styles['theme-section']}>
                        <div className={styles['section-header']}>
                            <h2 className={styles['section-title']}>
                                <BrushIcon className={styles['section-icon']} />
                                Custom Theme
                            </h2>
                            <p className={styles['section-description']}>
                                Create your own unique color scheme
                            </p>
                        </div>

                        <div className={styles['custom-theme-container']}>
                            <div className={styles['custom-toggle']}>
                                <label className={styles['toggle-label']}>
                                    <input
                                        type="checkbox"
                                        checked={isCustomMode}
                                        onChange={handleCustomModeToggle}
                                    />
                                    <span className={styles['toggle-slider']}></span>
                                    Enable Custom Colors
                                </label>
                            </div>

                            {isCustomMode && (
                                <div className={styles['custom-colors']}>
                                    <div className={styles['color-input-group']}>
                                        <label className={styles['color-label']}>
                                            Primary Color
                                            <div className={styles['color-input-container']}>
                                                <input
                                                    type="color"
                                                    value={customPrimary}
                                                    onChange={(e) => handleCustomColorChange('primary', e.target.value)}
                                                    className={styles['color-input']}
                                                />
                                                <input
                                                    type="text"
                                                    value={customPrimary}
                                                    onChange={(e) => handleCustomColorChange('primary', e.target.value)}
                                                    className={styles['color-text-input']}
                                                    placeholder="#1D2B56"
                                                />
                                            </div>
                                        </label>
                                    </div>

                                    <div className={styles['color-input-group']}>
                                        <label className={styles['color-label']}>
                                            Light Variant
                                            <div className={styles['color-input-container']}>
                                                <input
                                                    type="color"
                                                    value={customPrimaryLight}
                                                    onChange={(e) => handleCustomColorChange('light', e.target.value)}
                                                    className={styles['color-input']}
                                                />
                                                <input
                                                    type="text"
                                                    value={customPrimaryLight}
                                                    onChange={(e) => handleCustomColorChange('light', e.target.value)}
                                                    className={styles['color-text-input']}
                                                    placeholder="#2a3a6a"
                                                />
                                            </div>
                                        </label>
                                    </div>

                                    <div className={styles['color-input-group']}>
                                        <label className={styles['color-label']}>
                                            Dark Variant
                                            <div className={styles['color-input-container']}>
                                                <input
                                                    type="color"
                                                    value={customPrimaryDark}
                                                    onChange={(e) => handleCustomColorChange('dark', e.target.value)}
                                                    className={styles['color-input']}
                                                />
                                                <input
                                                    type="text"
                                                    value={customPrimaryDark}
                                                    onChange={(e) => handleCustomColorChange('dark', e.target.value)}
                                                    className={styles['color-text-input']}
                                                    placeholder="#162142"
                                                />
                                            </div>
                                        </label>
                                    </div>

                                    <button
                                        onClick={handleAutoGenerate}
                                        className={styles['auto-generate-btn']}
                                    >
                                        <RefreshIcon />
                                        Auto-Generate Variants
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Preview Section */}
                    {showPreview && (
                        <div className={styles['theme-section']}>
                            <div className={styles['section-header']}>
                                <h2 className={styles['section-title']}>Theme Preview</h2>
                                <p className={styles['section-description']}>
                                    See how your selected theme looks across different elements
                                </p>
                            </div>

                            <div className={styles['theme-preview-container']}>
                                <div className={styles['preview-elements']}>
                                    <div className={`${styles['preview-button']} ${styles.primary}`}>
                                        Primary Button
                                    </div>
                                    <div className={`${styles['preview-button']} ${styles.secondary}`}>
                                        Secondary Button
                                    </div>
                                    <div className={styles['preview-card']}>
                                        <div className={styles['preview-card-header']}>
                                            Sample Card
                                        </div>
                                        <div className={styles['preview-card-content']}>
                                            This is how cards will look with your selected theme.
                                        </div>
                                    </div>
                                    <div className={styles['preview-navbar']}>
                                        <div className={`${styles['preview-nav-item']} ${styles.active}`}>Dashboard</div>
                                        <div className={styles['preview-nav-item']}>Reports</div>
                                        <div className={styles['preview-nav-item']}>Settings</div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Action Buttons */}
                    <div className={styles['theme-actions']}>
                        <button
                            onClick={handleSaveTheme}
                            className={`${styles['action-button']} ${styles.save} ${loading ? styles.loading : ''}`}
                            disabled={loading}
                        >
                            {loading ? (
                                <>
                                    <div className={styles.loadingSpinner} />
                                    Saving...
                                </>
                            ) : (
                                <>
                                    <SaveIcon />
                                    Save Theme
                                </>
                            )}
                        </button>
                        <button
                            onClick={handleResetTheme}
                            className={`${styles['action-button']} ${styles.reset} ${loading ? styles.loading : ''}`}
                            disabled={loading}
                        >
                            {loading ? (
                                <>
                                    <div className={styles.loadingSpinner} />
                                    Resetting...
                                </>
                            ) : (
                                <>
                                    <RefreshIcon />
                                    Reset to Default
                                </>
                            )}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ThemeManagement;
